import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Link,
  Snackbar,
  Container,
  Slide,
  Stack,
  alpha,
  Tooltip,
  Divider,
  FormControl,
  Select,
  MenuItem,
  TablePagination,


} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Storage as StorageIcon,
  Edit as EditIcon
} from '@mui/icons-material';

import { Container as ContainerType, Locker, UpdateContainerData } from '../../types/locker';
import { useTranslation } from 'react-i18next';
import { containerService } from '../../services/api';
import { Autocomplete } from "@react-google-maps/api";
import { useRef } from "react";

interface ContainerDetailProps {
  viewOnly?: boolean;
}

const ContainerDetail: React.FC<ContainerDetailProps> = ( { viewOnly = false } ) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ container, setContainer ] = useState<ContainerType | null>( null );
  const [ loading, setLoading ] = useState( false );
  const [ toast, setToast ] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>( {
    open: false,
    message: '',
    severity: 'success'
  } );
  const [ lockersId, setLockersId ] = useState( 0 );
  const [ formData, setFormData ] = useState<UpdateContainerData>( {
    containerNumber: '',
    containerName: '',
    serialNumber: '',
    location: '',
    address: '',
    latitude: '',
    longitude: '',
    zipCode: '',
    imeiNumber: ''
  } );
  const [ lockers, setLockers ] = useState<Locker[]>( [] );
  const [ openLockerDialog, setOpenLockerDialog ] = useState( false );
  const [ deleteLockerDialogOpen, setDeleteLockerDialogOpen ] = useState( false );
  const [ lockerToDelete, setLockerToDelete ] = useState<Locker | null>( null );
  const [ statusDialogOpen, setStatusDialogOpen ] = useState( false );
  const [ lockerToUpdate, setLockerToUpdate ] = useState<{ locker: Locker; newStatus: string } | null>( null );
  const [ newLocker, setNewLocker ] = useState<Partial<Locker>>( {
    lockerId: '',
    price: null,
    status: 'active',
    availability: 'available',
    offerId: '',
  } );

  const [ selectedUser, setSelectedUser ] = useState<Locker | null>( null );
  const [ openDialog, setOpenDialog ] = useState( false );
  const [ dialogType, setDialogType ] = useState<'view' | 'edit' | 'sales' | 'purchases' | 'delete' | null>( null );
  const autoCompleteRef = useRef<any>( null );
  const [ autoCompleteEnabled, setAutoCompleteEnabled ] = useState( true )
  const addressInputRef = useRef<HTMLInputElement | null>( null );
  const [ page, setPage ] = React.useState( 0 );
  const [ rowsPerPage, setRowsPerPage ] = React.useState( 10 );


  const isGoogleAvailable = !!window.google;

  useEffect( () => {
    // Trigger automatically when API key is wrong → InvalidKeyMapError
    ( window as any ).gm_authFailure = () => {
      console.error( "❌ Invalid Google API Key → Switching to manual address input" );
      setAutoCompleteEnabled( false );

      // Reset only address, not other fields
      setTimeout( () => {
        if ( addressInputRef.current ) {
          const len = addressInputRef.current.value.length;

          addressInputRef.current.focus();
          addressInputRef.current.setSelectionRange( len, len ); // cursor stays at end
        }
      }, 50 );;
    };
  }, [] );


  useEffect( () => {
    const fetchContainer = async () => {
      if ( id ) {
        try {
          setLoading( true );
          const response = await containerService.getContainer( id );
          if ( response.success ) {
            setContainer( response.data );
            setFormData( {
              containerNumber: response.data.containerNumber,
              containerName: response.data.containerName,
              serialNumber: response.data.serialNumber,
              location: response.data.location,
              address: response.data.address,
              latitude: response.data.latitude,
              longitude: response.data.longitude,
              zipCode: response.data.zipCode,
              imeiNumber: response.data.imeiNumber
            } );
            setLockers( response.data.lockers || [] );
          } else {
            setToast( {
              open: true,
              message: response.message || 'Failed to fetch container',
              severity: 'error'
            } );
          }
        } catch ( error ) {
          console.error( 'Error fetching container:', error );
          setToast( {
            open: true,
            message: 'Error fetching container',
            severity: 'error'
          } );
        } finally {
          setLoading( false );
        }
      }
    };
    fetchContainer();
  }, [ id, lockersId ] );

  const handleChange = ( field: keyof UpdateContainerData, value: any ) => {
    setFormData( { ...formData, [ field ]: value } );
  };

  const handleLockerChange = ( field: keyof Locker, value: any ) => {
    setNewLocker( { ...newLocker, [ field ]: value } );
  };

  const handleAddLocker = async () => {
    if ( newLocker.lockerId && id ) {
      try {
        setLoading( true );
        const response = await containerService.createLocker( {
          containerId: Number( id ),
          lockerId: newLocker.lockerId,
          price: newLocker.price || null,
        } );

        if ( response.success ) {
          // Refresh container data to get updated lockers
          const containerResponse = await containerService.getContainer( id );
          if ( containerResponse.success ) {
            setContainer( containerResponse.data );
            setLockers( containerResponse.data.lockers || [] );
          }
          // Clear form data and close dialog
          setNewLocker( {
            lockerId: '',
            price: null,
            status: 'active',
            availability: 'available',
            offerId: '',
          } );
          setOpenLockerDialog( false );
          setToast( {
            open: true,
            message: t( 'locker.create.success' ),
            severity: 'success'
          } );
        } else {
          setToast( {
            open: true,
            message: response.message || t( 'locker.error.create' ),
            severity: 'error'
          } );
        }
      } catch ( error ) {
        console.error( 'Error creating locker:', error );
        setToast( {
          open: true,
          message: t( 'locker.error.create' ),
          severity: 'error'
        } );
      } finally {
        setLoading( false );
      }
    } else {
      setToast( {
        open: true,
        message: t( 'locker.error.create' ),
        severity: 'error'
      } );
    }
  };

  const handleOpenLockerDialog = () => {
    // Clear form data when opening dialog
    setNewLocker( {
      lockerId: '',
      price: null,
      status: 'active',
      availability: 'available',
      offerId: '',
    } );
    setOpenLockerDialog( true );
  };

  const handleCloseLockerDialog = () => {
    // Clear form data when closing dialog
    setNewLocker( {
      lockerId: '',
      price: null,
      status: 'active',
      availability: 'available',
      offerId: '',
    } );
    setOpenLockerDialog( false );
  };
  const handleChangePage = (
    event: unknown,
    newPage: number
  ) => {
    setPage( newPage );
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage( parseInt( event.target.value, 10 ) );
    setPage( 0 );
  };


  const handleRemoveLocker = ( locker: Locker ) => {
    setLockerToDelete( locker );
    setDeleteLockerDialogOpen( true );
  };


  const handleEditUser = ( locker: Locker ) => {
    const userDetails: Locker = {
      ...locker,
      // countryCode: user.countryCode || '',
      // phoneNumber: user.phoneNumber || '',
      // name: `${ user.firstName } ${ user.lastName }`,
      // role: 'user', // Default role since it's not in the API response
      // sales: [],
      // purchases: []
    };
    setSelectedUser( userDetails );
    setDialogType( 'edit' );
    setOpenDialog( true );
    // Set the current user status in editForm
    // setEditForm( {
    //   password: '',
    //   status: user.status as 'active' | 'inactive'
    // } );
  }

  const handleDeleteLockerConfirm = async () => {
    if ( !lockerToDelete ) return;

    try {
      setLoading( true );
      const response = await containerService.updateLockerStatus( {
        lockerId: Number( lockerToDelete.id ),
        status: 'delete'
      } );

      if ( response.success ) {
        // Refresh container data to get updated lockers
        const containerResponse = await containerService.getContainer( id || '' );
        if ( containerResponse.success ) {
          setContainer( containerResponse.data );
          setLockers( containerResponse.data.lockers || [] );
        }
        setToast( {
          open: true,
          message: t( 'locker.delete.success' ),
          severity: 'success'
        } );
      } else {
        setToast( {
          open: true,
          message: response.message || t( 'locker.error.delete' ),
          severity: 'error'
        } );
      }
    } catch ( error ) {
      console.error( 'Error deleting locker:', error );
      setToast( {
        open: true,
        message: t( 'locker.error.delete' ),
        severity: 'error'
      } );
    } finally {
      setLoading( false );
      setDeleteLockerDialogOpen( false );
      setLockerToDelete( null );
    }
  };

  const handleDeleteLockerCancel = () => {
    setDeleteLockerDialogOpen( false );
  };

  const handleStatusClick = ( locker: Locker, newStatus: string ) => {
    setLockerToUpdate( { locker, newStatus } );
    setStatusDialogOpen( true );
  };

  const handleStatusConfirm = async () => {
    if ( !lockerToUpdate ) return;

    try {
      setLoading( true );
      const response = await containerService.updateLockerStatus( {
        lockerId: Number( lockerToUpdate.locker.id ),
        status: lockerToUpdate.newStatus
      } );

      if ( response.success ) {
        // Refresh container data to get updated lockers
        const containerResponse = await containerService.getContainer( id || '' );
        if ( containerResponse.success ) {
          setContainer( containerResponse.data );
          setLockers( containerResponse.data.lockers || [] );
        }
        setToast( {
          open: true,
          message: 'Locker status updated successfully',
          severity: 'success'
        } );
      } else {
        setToast( {
          open: true,
          message: response.message || t( 'locker.error.status' ),
          severity: 'error'
        } );
      }
    } catch ( error ) {
      console.error( 'Error updating locker status:', error );
      setToast( {
        open: true,
        message: t( 'locker.error.status' ),
        severity: 'error'
      } );
    } finally {
      setLoading( false );
      setStatusDialogOpen( false );
      setLockerToUpdate( null );
    }
  };

  const handleStatusCancel = () => {
    setStatusDialogOpen( false );
  };

  const handleSaveChanges = async ( updatedUser: any ) => {
    try {
      setLoading( true );

      const response = await containerService.updateLocker( updatedUser );
      if ( response.success ) {
        setToast( {
          open: true,
          message: 'Locker updated successfully!',
          severity: 'success',
        } );
        setOpenDialog( false );
        // refresh data
        setLockersId( Date.now() ); // trigger re-fetch in useEffect
      } else {
        setToast( {
          open: true,
          message: response.message || 'Failed to update locker',
          severity: 'error',
        } );
      }
    } catch ( err ) {
      console.error( err );
      setToast( {
        open: true,
        message: 'Something went wrong!',
        severity: 'error',
      } );
    } finally {
      setLoading( false );
    }
  };


  const handleSubmit = async ( e: React.FormEvent ) => {
    e.preventDefault();
    if ( !id ) return;
    try {
      setLoading( true );
      const response = await containerService.updateContainer( id, {
        containerNumber: formData.containerNumber,
        containerName: formData.containerName,
        serialNumber: formData.serialNumber,
        location: formData.location,
        address: formData.address,
        latitude: formData.latitude,
        longitude: formData.longitude
      } );

      if ( response.success ) {
        setToast( {
          open: true,
          message: 'Container updated successfully',
          severity: 'success'
        } );
      } else {
        setToast( {
          open: true,
          message: response.message || 'Failed to update container',
          severity: 'error'
        } );
      }
    } catch ( err ) {
      console.error( 'Failed to update container:', err );
      setToast( {
        open: true,
        message: 'Error updating container',
        severity: 'error'
      } );
    } finally {
      setLoading( false );
    }
  };
  const handlePlaceSelect = () => {

    if ( autoCompleteRef !== null ) {


      if ( !autoCompleteRef?.current ) {
        console.warn( 'Autocomplete not loaded yet.' );
        return;
      }

      const place = autoCompleteRef?.current?.getPlace();
      if ( !place ) {
        console.warn( 'No place selected or API not loaded.' );
        return;
      }
      if ( !place ) return;
      const location = place.geometry?.location;

      setFormData( {
        ...formData,
        address: place.formatted_address || '',
        latitude: location?.lat() || 0,
        longitude: location?.lng() || 0,
      } );


    } else { return }
  };


  const handleCloseToast = () => {
    setToast( { ...toast, open: false } );
  };

  if ( loading && !container ) {
    return (
      <Container maxWidth="lg" sx={ { py: 4 } }>
        <Box sx={ {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          gap: 2
        } }>
          <CircularProgress size={ 60 } />
          <Typography variant="h6" color="text.secondary">
            Loading container details...
          </Typography>
        </Box>
      </Container>
    );
  }

  if ( !container ) {
    return (
      <Container maxWidth="lg" sx={ { py: 4 } }>
        <Alert severity="info">Container not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={ false } sx={ { py: 4, width: '100%' } }>
      <Slide direction="down" in={ true } timeout={ 600 }>
        < Box>
          {/* Header Section */ }
          <Box sx={ { mb: 4 } }>
            <Stack direction="row" alignItems="center" spacing={ 2 } sx={ { mb: 2 } }>
              <Button
                variant="outlined"
                startIcon={ <ArrowBackIcon /> }
                onClick={ () => navigate( '/lockers' ) }
                sx={ { mr: 2 } }
              >
                Back
              </Button>
              <Box sx={ {
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              } }>
                <StorageIcon sx={ { fontSize: 28 } } />
              </Box>
              <Box sx={ { flex: 1 } }>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  { viewOnly ? t( 'containers.management.viewContainer' ) : t( 'containers.management.editContainer' ) }
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  { container.containerName } • { container.containerNumber }
                </Typography>
              </Box>
              { !viewOnly && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={ <SaveIcon /> }
                  onClick={ handleSubmit }
                  disabled={ loading }
                  sx={ {
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                    '&:hover': {
                      boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)'
                    }
                  } }
                >
                  { loading ? 'Saving...' : t( 'common.save' ) }
                </Button>
              ) }
            </Stack>
          </Box>

          {/* Container Details */ }
          <Paper
            elevation={ 0 }
            sx={ {
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              p: 4,
              mb: 4
            } }
          >
            <Typography variant="h6" fontWeight="600" sx={ { mb: 3 } }>
              Container Information
            </Typography>
            <Grid container spacing={ 3 }>
              <Grid item xs={ 12 } md={ 6 }>
                <TextField
                  fullWidth
                  required
                  label={ t( 'containers.management.containerNumber' ) }
                  value={ formData.containerNumber }
                  onChange={ ( e ) => handleChange( 'containerNumber', e.target.value ) }
                  disabled={ viewOnly }
                />
              </Grid>
              <Grid item xs={ 12 } md={ 6 }>
                <TextField
                  fullWidth
                  required
                  label={ t( 'containers.management.containerName' ) }
                  value={ formData.containerName }
                  onChange={ ( e ) => handleChange( 'containerName', e.target.value ) }
                  disabled={ viewOnly }
                />
              </Grid>
              <Grid item xs={ 12 } md={ 6 }>
                <TextField
                  fullWidth
                  required
                  label={ t( 'containers.management.serialNumber' ) }
                  value={ formData.serialNumber }
                  onChange={ ( e ) => handleChange( 'serialNumber', e.target.value ) }
                  disabled={ viewOnly }
                />
              </Grid>
              <Grid item xs={ 12 } md={ 6 }>
                <TextField
                  fullWidth
                  required
                  label={ t( 'containers.management.imeiNumber' ) }
                  value={ formData.imeiNumber }
                  onChange={ ( e ) => handleChange( 'imeiNumber', e.target.value ) }
                  disabled={ viewOnly }
                />
              </Grid>
              {/* <Grid item xs={ 12 } md={ 6 }>
                <TextField
                  fullWidth
                  required
                  label={ t( 'containers.management.address' ) }
                  value={ formData.address }
                  onChange={ ( e ) => handleChange( 'address', e.target.value ) }
                  disabled={ viewOnly }
                  multiline
                  rows={ 3 }
                />
              </Grid> */}
              { autoCompleteEnabled ? ( <Grid item xs={ 12 } md={ 6 }>



                <Autocomplete
                  onLoad={ ( ref ) => ( autoCompleteRef.current = ref ) }
                  onPlaceChanged={ handlePlaceSelect } // use existing function
                >
                  <TextField
                    fullWidth
                    label={ t( 'containers.management.address' ) }
                    value={ formData.address }
                    placeholder="Search & select location or type your address"
                    required
                    sx={ { background: "#fff" } }
                    onChange={ ( e ) => {
                      setFormData( {
                        ...formData,
                        address: e.target.value,

                      } )
                      handleChange( 'address', e.target.value )
                    }


                    }
                    disabled={ viewOnly }
                  />
                </Autocomplete>
              </Grid> ) : ( <Grid item xs={ 12 } md={ 6 }>  <TextField

                fullWidth
                label={ t( 'containers.management.address' ) }
                inputRef={ addressInputRef }
                value={ formData.address }
                placeholder="Enter address manually (Google disabled)"
                onChange={ ( e ) =>
                  setFormData( {
                    ...formData,
                    address: e.target.value,

                  } )
                }
                disabled={ viewOnly }
              />
              </Grid>
              ) }

              <Grid item xs={ 12 } md={ 6 }>
                <TextField
                  fullWidth
                  required
                  label={ t( 'containers.management.zipCode' ) }
                  value={ formData.zipCode }
                  onChange={ ( e ) => handleChange( 'zipCode', e.target.value ) }
                  disabled={ viewOnly }
                />
              </Grid>

            </Grid>
          </Paper>

          {/* Lockers Section */ }
          <Typography variant="h6" fontWeight="600">
            { t( 'containers.management.lockers' ) } ({ lockers.length })
          </Typography>


          <Box >
            {/* <Stack direction="row" justifyContent="space-between" alignItems="center">

                { !viewOnly && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={ <AddIcon /> }
                    onClick={ handleOpenLockerDialog }
                    sx={ {
                      px: 3,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                      '&:hover': {
                        boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)'
                      }
                    } }
                  >
                    { t( 'containers.management.addLocker' ) }
                  </Button>
                ) }
              </Stack> */}
          </Box>
          <TableContainer
            component={ Paper }
            sx={ {
              borderRadius: 3,
              overflow: "hidden",
              mt: 2,
            } }
          >
            <Table>
              <TableHead>
                <TableRow
                  sx={ {
                    backgroundColor: "primary.main",
                    "& th": {
                      color: "white",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      textAlign: "center",
                    },
                  } }
                >
                  <TableCell>{ t( "containers.management.lockerId" ) }</TableCell>
                  <TableCell>{ t( "containers.management.chargesPerHour" ) }</TableCell>
                  <TableCell>{ t( "containers.management.occupied" ) }</TableCell>
                  <TableCell>{ t( "orders.order_number" ) }</TableCell>
                  <TableCell>{ t( "common.status" ) }</TableCell>
                  { !viewOnly && (
                    <TableCell align="center">{ t( "common.actions" ) }</TableCell>
                  ) }
                </TableRow>
              </TableHead>

              <TableBody>
                { lockers
                  .slice( page * rowsPerPage, page * rowsPerPage + rowsPerPage )
                  .map( ( locker ) => (

                    <TableRow
                      key={ locker.id }
                      sx={ {
                        "&:hover": {
                          backgroundColor: ( theme ) =>
                            alpha( theme.palette.primary.main, 0.04 ),
                        },
                        transition: "background-color 0.2s ease",
                      } }
                    >
                      {/* Locker ID */ }
                      <TableCell align="center">
                        <Typography variant="subtitle2" >
                          { locker.lockerId }
                        </Typography>
                      </TableCell>

                      {/* Charges */ }
                      <TableCell align="center">
                        <Typography variant="body2" >
                          ${ locker.price }/hr
                        </Typography>
                      </TableCell>

                      {/* Occupied */ }
                      <TableCell align="center">
                        <Chip
                          label={
                            locker.availability === "occupied"
                              ? t( "common.yes" )
                              : t( "common.no" )
                          }
                          color={
                            locker.availability === "occupied" ? "error" : "success"
                          }
                          size="small"
                          variant="outlined"
                          sx={ { fontWeight: 600 } }
                        />
                      </TableCell>

                      {/* Order ID */ }
                      <TableCell align="center">
                        { locker.offerId ? (
                          <Link
                            component="button"
                            variant="body2"
                            onClick={ () => navigate( `/orders/${ locker.offerId }` ) }

                          >
                            { locker.offerId }
                          </Link>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        ) }
                      </TableCell>

                      {/* Status */ }
                      <TableCell align="center">
                        <Chip
                          label={ locker.status === "active" ? "Active" : "Inactive" }
                          color={ locker.status === "active" ? "success" : "error" }
                          size="small"
                          sx={ { fontWeight: 600 } }
                          onClick={ () =>
                            handleStatusClick(
                              locker,
                              locker.status === "active" ? "inactive" : "active"
                            )
                          }
                          clickable
                        />
                      </TableCell>

                      {/* Actions */ }
                      { !viewOnly && (
                        <TableCell align="center">
                          <Box sx={ { display: "flex", justifyContent: "center", gap: 1 } }>
                            <Tooltip title="Delete" arrow>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={ () => handleRemoveLocker( locker ) }
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Edit Locker" arrow>
                              <IconButton
                                size="small"
                                onClick={ () => handleEditUser( locker ) }
                                color="primary"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      ) }
                    </TableRow>
                  ) ) }
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={ [ 5, 10, 25 ] }
              component="div"
              count={ lockers.length }
              rowsPerPage={ rowsPerPage }
              page={ page }
              onPageChange={ handleChangePage }
              onRowsPerPageChange={ handleChangeRowsPerPage }
            />

          </TableContainer>


          { lockers.length === 0 && (
            <Box sx={ {
              textAlign: 'center',
              py: 8,
              color: 'text.secondary'
            } }>
              <StorageIcon sx={ { fontSize: 64, mb: 2, opacity: 0.5 } } />
              <Typography variant="h6" sx={ { mb: 1 } }>
                No lockers found
              </Typography>
              <Typography variant="body2">
                { viewOnly ? 'This container has no lockers' : 'Get started by adding your first locker' }
              </Typography>
            </Box>
          ) }

        </Box>
      </Slide>

      {/* Add Locker Dialog */ }
      <Dialog
        open={ openLockerDialog }
        onClose={ handleCloseLockerDialog }
        maxWidth="sm"
        fullWidth
        PaperProps={ {
          sx: {
            borderRadius: 3,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          }
        } }
      >
        <DialogTitle sx={ {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '1.3rem',
          textAlign: 'center',
          py: 2
        } }>
          { t( 'containers.management.addLocker' ) }
        </DialogTitle>
        <DialogContent sx={ { p: 4 } }>
          <Box sx={ { mb: 3 } }>
            {/* <Typography variant="h6" sx={{
              mb: 2,
              color: 'text.primary',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <StorageIcon color="primary" />
              Add New Locker
            </Typography> */}
          </Box>

          <Grid container spacing={ 3 }>
            <Grid item xs={ 12 }>
              <TextField
                fullWidth
                required
                label={ t( 'containers.management.lockerId' ) }
                value={ newLocker.lockerId }
                onChange={ ( e ) => handleLockerChange( 'lockerId', e.target.value ) }
                variant="outlined"
                placeholder="Enter locker ID"
                sx={ {
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: 'primary.main',
                  }
                } }
              />
            </Grid>
            <Grid item xs={ 12 }>
              <TextField
                fullWidth
                required
                label={ t( 'containers.management.chargesPerHour' ) }
                value={ newLocker.price }
                onChange={ ( e ) => handleLockerChange( 'price', e.target.value ) }
                variant="outlined"
                placeholder="Enter hourly rate"
                type="number"
                inputProps={ { min: 0, step: 0.01 } }
                sx={ {
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: 'primary.main',
                  }
                } }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={ { p: 3, pt: 1, gap: 2 } }>
          <Button
            onClick={ handleCloseLockerDialog }
            variant="outlined"
            disabled={ loading }
            sx={ {
              minWidth: 120,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            } }
          >
            { t( 'common.cancel' ) }
          </Button>
          <Button
            onClick={ handleAddLocker }
            variant="contained"
            disabled={ loading || !newLocker.lockerId || !newLocker.price }
            startIcon={ loading ? <CircularProgress size={ 20 } /> : <AddIcon /> }
            sx={ {
              minWidth: 120,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
              '&:disabled': {
                background: 'grey.400',
                color: 'grey.600'
              }
            } }
          >
            { loading ? t( 'common.adding' ) : t( 'common.add' ) }
          </Button>
        </DialogActions>

      </Dialog>



      {/* Edit User Dialog */ }
      <Dialog
        open={ openDialog && dialogType === 'edit' }
        onClose={ () => setOpenDialog( false ) }
        maxWidth="md"
        fullWidth
        PaperProps={ {
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          },
        } }
      >
        <DialogTitle
          sx={ {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '1.3rem',
            textAlign: 'center',
            py: 2,
          } }
        >
          Edit Locker
        </DialogTitle>

        <DialogContent sx={ { p: 4 } }>
          { selectedUser && (
            <Box sx={ { mt: 2 } }>
              <Box sx={ { mb: 4 } }>
                <Typography
                  variant="h6"
                  sx={ {
                    mb: 2,
                    color: 'text.primary',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  } }
                >
                  <Box
                    component="span"
                    sx={ {
                      width: 4,
                      height: 20,
                      backgroundColor: 'primary.main',
                      borderRadius: 1,
                    } }
                  />
                  Locker Information
                </Typography>
                <Divider sx={ { opacity: 0.3 } } />
              </Box>

              {/* Editable form fields */ }
              <Grid container spacing={ 3 }>
                {/* Locker ID */ }
                <Grid item xs={ 12 } md={ 6 }>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    sx={ { mb: 1, fontWeight: 600 } }
                  >
                    Locker ID
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={ selectedUser.lockerId || '' }
                    onChange={ ( e ) =>
                      setSelectedUser( { ...selectedUser, lockerId: e.target.value } )
                    }
                    sx={ {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'grey.50',
                      },
                    } }
                  />
                </Grid>

                {/* Charges */ }
                <Grid item xs={ 12 } md={ 6 }>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    sx={ { mb: 1, fontWeight: 600 } }
                  >
                    Charges (Per Hour)
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    type="number"
                    value={ selectedUser.price || '' }
                    onChange={ ( e ) =>
                      setSelectedUser( {
                        ...selectedUser,
                        price: Number( e.target.value ),
                      } )
                    }
                    sx={ {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'grey.50',
                      },
                    } }
                  />
                </Grid>

                {/* Status */ }
                <Grid item xs={ 12 } md={ 6 }>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    sx={ { mb: 1, fontWeight: 600 } }
                  >
                    Status
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={ selectedUser.status || '' }
                      onChange={ ( e ) =>
                        setSelectedUser( {
                          ...selectedUser,
                          status: e.target.value,
                        } )
                      }
                      variant="outlined"
                      sx={ {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: 'grey.50',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                          },
                        },
                      } }
                    >
                      <MenuItem value="active">
                        <Chip label="Active" color="success" size="small" />
                      </MenuItem>
                      <MenuItem value="inactive">
                        <Chip label="Inactive" color="error" size="small" />
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          ) }
        </DialogContent>

        <DialogActions sx={ { p: 3, pt: 1, gap: 2 } }>
          <Button
            onClick={ () => setOpenDialog( false ) }
            disabled={ loading }
            variant="outlined"
            sx={ {
              minWidth: 120,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            } }
          >
            Cancel
          </Button>

          <Button
            onClick={ () => handleSaveChanges( selectedUser ) }
            variant="contained"
            disabled={ loading }
            sx={ {
              minWidth: 120,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
            } }
          >
            { loading ? 'Saving...' : 'Save' }
          </Button>
        </DialogActions>
      </Dialog>


      {/* Delete Locker Dialog */ }
      <Dialog
        open={ deleteLockerDialogOpen }
        onClose={ handleDeleteLockerCancel }
        maxWidth="sm"
        fullWidth
      // PaperProps={{
      //   sx: {
      //     borderRadius: 3,
      //     overflow: 'hidden',
      //     maxHeight: '60vh'
      //   }
      // }}
      >
        <DialogTitle sx={ {
          bgcolor: 'error.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 2.5,
          px: 3
        } }>
          <DeleteIcon />
          Delete Locker
        </DialogTitle>
        <DialogContent style={ { paddingTop: "20px" } }>
          { lockerToDelete && (
            <Typography variant="body1">
              { t( 'locker.delete.confirmation', { id: lockerToDelete.lockerId } ) }
            </Typography>
          ) }
        </DialogContent>
        <DialogActions>
          <Button onClick={ handleDeleteLockerCancel } disabled={ loading } variant="outlined"
            sx={ {
              minWidth: 100,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            } }>
            { t( 'common.cancel' ) }
          </Button>
          <Button
            onClick={ handleDeleteLockerConfirm }
            color="error"
            variant="contained"
            disabled={ loading }
            sx={ {
              minWidth: 120,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            } }
          >
            { loading ? <CircularProgress size={ 24 } /> : t( 'common.delete' ) }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */ }
      <Dialog
        open={ statusDialogOpen }
        onClose={ handleStatusCancel }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={ {
          bgcolor: lockerToUpdate?.newStatus === 'active' ? 'success.main' : 'error.main',
          textTransform: 'none',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 2.5,
          px: 3
        } } >
          { lockerToUpdate?.newStatus === 'active' ? 'Activate Locker' : 'Deactivate Locker' }
        </DialogTitle>
        <DialogContent style={ { paddingTop: "20px" } }>
          { lockerToUpdate && (
            <Typography>
              { `Are you sure you want to ${ lockerToUpdate?.newStatus === 'active' ? 'Activate' : 'Deactivate' } this locker?` }
            </Typography>
          ) }
        </DialogContent>
        <DialogActions>
          <Button onClick={ handleStatusCancel } disabled={ loading } variant="outlined"
            sx={ {
              minWidth: 100,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            } }>
            Cancel
          </Button>
          <Button
            onClick={ handleStatusConfirm }
            color={ lockerToUpdate?.newStatus === 'active' ? 'success' : 'error' }
            variant="contained"
            disabled={ loading }
            sx={ {
              minWidth: 120,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            } }
          >
            { loading ? <CircularProgress size={ 24 } /> : ( lockerToUpdate?.newStatus === 'active' ? 'Activate' : 'Deactivate' ) }
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={ toast.open }
        autoHideDuration={ 6000 }
        onClose={ handleCloseToast }
        anchorOrigin={ { vertical: 'top', horizontal: 'right' } }
      >
        <Alert onClose={ handleCloseToast } severity={ toast.severity } sx={ { width: '100%' } }>
          { toast.message }
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ContainerDetail; 